import {
  EDIT_COMPANY,
  EDIT_COMPANY_FAILED,
  EDIT_COMPANY_SUCCESS,
  FETCH_COMPANIES,
  FETCH_COMPANIES_FAILED,
  FETCH_COMPANIES_SUCCESS,
  SET_SELECTED_COMPANY,
} from "../store/types";
import { supabase } from '../config/supabaseClient';
import { GetDistance } from "../other/GeoFunctions";

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const withIds = (rows) => (rows || []).map((row) => ({ ...row, id: row.id }));

const pickupMatchesArea = (pickup, area) => {
  if (!pickup || !area) return true;
  if (area.area_type === 'city' && area.city && pickup.city) {
    return String(area.city).toLowerCase() === String(pickup.city).toLowerCase();
  }
  if (area.area_type === 'radius' && area.center_lat && area.center_lng && area.radius_km) {
    const distance = GetDistance(
      Number(pickup.lat),
      Number(pickup.lng),
      Number(area.center_lat),
      Number(area.center_lng)
    );
    return distance <= Number(area.radius_km);
  }
  return true;
};

export const setSelectedCompany = (company) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_COMPANY,
    payload: company
  });
};

export const fetchCompanies = () => async (dispatch) => {
  dispatch({
    type: FETCH_COMPANIES,
    payload: null
  });
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('status', 'active')
      .order('name', { ascending: true });
    if (error) throw error;
    dispatch({
      type: FETCH_COMPANIES_SUCCESS,
      payload: withIds(data)
    });
  } catch (error) {
    dispatch({
      type: FETCH_COMPANIES_FAILED,
      payload: error.message || error
    });
  }
};

export const fetchCompaniesForLocation = (pickup) => async (dispatch) => {
  dispatch({
    type: FETCH_COMPANIES,
    payload: null
  });
  try {
    const { data: companies, error } = await supabase
      .from('companies')
      .select('*')
      .eq('status', 'active')
      .order('name', { ascending: true });
    if (error) throw error;

    const { data: serviceAreas, error: areaError } = await supabase
      .from('company_service_areas')
      .select('*')
      .eq('active', true);
    if (areaError) throw areaError;

    const areasByCompany = (serviceAreas || []).reduce((result, area) => {
      result[area.company_id] = result[area.company_id] || [];
      result[area.company_id].push(area);
      return result;
    }, {});

    const filtered = (companies || []).filter((company) => {
      const areas = areasByCompany[company.id] || [];
      return !areas.length || areas.some((area) => pickupMatchesArea(pickup, area));
    });

    dispatch({
      type: FETCH_COMPANIES_SUCCESS,
      payload: withIds(filtered)
    });
  } catch (error) {
    dispatch({
      type: FETCH_COMPANIES_FAILED,
      payload: error.message || error
    });
  }
};

export const registerCompany = (company) => async (dispatch) => {
  dispatch({
    type: EDIT_COMPANY,
    payload: company
  });
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) throw new Error('Sign in before registering a taxi company.');

    const slugBase = slugify(company.slug || company.name);
    if (!slugBase) throw new Error('Company name is required.');

    const insertCompany = {
      name: company.name,
      slug: `${slugBase}-${Date.now().toString(36)}`,
      legal_name: company.legal_name || company.legalName || null,
      registration_number: company.registration_number || company.registrationNumber || null,
      tax_id: company.tax_id || company.taxId || null,
      email: company.email || user.email || null,
      phone: company.phone || null,
      address: company.address || null,
      logo_url: company.logo_url || company.logoUrl || null,
      status: company.status || 'pending',
      owner_user_id: user.id,
      default_language: company.default_language || company.defaultLanguage || null,
      timezone: company.timezone || null,
      currency: company.currency || null,
    };

    const { data: createdCompany, error } = await supabase
      .from('companies')
      .insert(insertCompany)
      .select()
      .single();
    if (error) throw error;

    const { error: memberError } = await supabase
      .from('company_members')
      .insert({
        company_id: createdCompany.id,
        user_id: user.id,
        role: 'owner',
        status: 'active'
      });
    if (memberError) throw memberError;

    const { error: settingsError } = await supabase
      .from('company_settings')
      .insert({
        company_id: createdCompany.id,
        settings: company.settings || {}
      });
    if (settingsError) throw settingsError;

    dispatch({
      type: EDIT_COMPANY_SUCCESS,
      payload: createdCompany
    });
    return { success: true, company: createdCompany };
  } catch (error) {
    dispatch({
      type: EDIT_COMPANY_FAILED,
      payload: error.message || error
    });
    return { success: false, error };
  }
};
